import { readFileSync, writeFileSync, existsSync, mkdirSync, copyFileSync } from 'fs';
import { resolve, dirname, join } from 'path';
import { fileURLToPath } from 'url';
import { createRequire } from 'module';
import yaml from 'js-yaml';
import { createProgram, type CommandHandlers } from './generated/program.js';
import { commandAudit } from './commands/audit.js';
import { commandImplement } from './commands/implement.js';
import { commandInsights } from './commands/insights.js';
import { resolvedDsl } from './generated/dsl/index.js';

const require = createRequire(import.meta.url);
const pkg = require('../package.json') as { version: string };

const __filename = fileURLToPath(import.meta.url);
const __dirname = dirname(__filename);

interface EmbedocDirs {
  renderersDir: string;
  datasourcesDir: string;
  templatesDir: string;
}

function resolveEmbedocDirs(configPath: string): EmbedocDirs {
  const configDir = dirname(configPath);
  const raw = readFileSync(configPath, 'utf-8');
  const config = (yaml.load(raw) ?? {}) as Record<string, unknown>;

  const renderersDir = resolve(
    configDir,
    (config['renderers_dir'] as string) ?? '.embedoc/renderers',
  );
  const datasourcesDir = resolve(
    configDir,
    (config['datasources_dir'] as string) ?? '.embedoc/datasources',
  );
  const templatesDir = resolve(
    configDir,
    (config['templates_dir'] as string) ?? '.embedoc/templates',
  );

  return { renderersDir, datasourcesDir, templatesDir };
}

const handlers: CommandHandlers = {
  // ── audit ──────────────────────────────────────────────────────────────────
  audit: async (target, opts) => {
    await commandAudit(target, {
      adapter:      opts.adapter as string | undefined,
      model:        opts.model as string | undefined,
      showPrompt:   opts.showPrompt as boolean | undefined,
      failOn:       opts.failOn as 'warning' | 'error' | 'critical' | undefined,
      output:       opts.output as string | undefined,
      reportFormat: opts.reportFormat as 'json' | 'text' | 'yaml' | undefined,
    });
  },

  // ── implement ──────────────────────────────────────────────────────────────
  implement: async (description, opts) => {
    await commandImplement(description, {
      target:       opts.target as string | undefined,
      models:       opts.models as string | undefined,
      adapter:      opts.adapter as string | undefined,
      model:        opts.model as string | undefined,
      showPrompt:   opts.showPrompt as boolean | undefined,
      failOn:       opts.failOn as 'warning' | 'error' | 'critical' | undefined,
      output:       opts.output as string | undefined,
      reportFormat: opts.reportFormat as 'json' | 'text' | 'yaml' | undefined,
    });
  },

  // ── agents ─────────────────────────────────────────────────────────────────
  agents: async (opts) => {
    const YAML = await import('yaml');
    const format = opts.format ?? 'yaml';
    try {
      if (format === 'json') {
        console.log(JSON.stringify(resolvedDsl, null, 2));
      } else {
        console.log(YAML.stringify(resolvedDsl, { lineWidth: 120 }));
      }
    } catch (err) {
      console.error(`Failed to output DSL: ${(err as Error).message}`);
      process.exit(1);
    }
  },

  // ── insights ───────────────────────────────────────────────────────────────
  insights: async (opts) => {
    try {
      await commandInsights({
        format: opts.format as string | undefined,
        projectRoot: opts.projectRoot as string | undefined,
        config: opts.config as string | undefined,
      });
    } catch (error) {
      console.error('Insights failed:', error instanceof Error ? error.message : error);
      process.exit(1);
    }
  },

  // ── init ───────────────────────────────────────────────────────────────────
  init: async (config) => {
    const configPath = config ?? 'embedoc.config.yaml';
    const absConfigPath = resolve(configPath);
    if (!existsSync(absConfigPath)) {
      process.stderr.write(JSON.stringify({
        error: "config_not_found",
        message: `${configPath} not found. Run "npx embedoc init" first.`,
      }, null, 2) + "\n");
      process.exit(3);
    }

    const dirs = resolveEmbedocDirs(absConfigPath);

    console.log('Setting up litedbmodel-gen...\n');

    copyTemplate(dirs.templatesDir);
    updateRenderersIndex(dirs.renderersDir);
    updateDatasourcesIndex(dirs.datasourcesDir);
    updateConfig(absConfigPath);

    console.log('\nDone! Next steps:');
    console.log('  1. Edit embedoc.config.yaml — set path/database in the schema datasource');
    console.log('  2. npx embedoc generate --datasource schema  (create model files)');
    console.log('  3. npx embedoc build                         (fill in column definitions)');
  },
};

function copyTemplate(templatesDir: string): void {
  mkdirSync(templatesDir, { recursive: true });

  const destPath = join(templatesDir, 'model.hbs');
  if (existsSync(destPath)) {
    console.log(`  skip  ${destPath} (already exists)`);
    return;
  }

  const srcPath = resolvePackageTemplate();
  copyFileSync(srcPath, destPath);
  console.log(`  create  ${destPath}`);
}

function resolvePackageTemplate(): string {
  const candidates = [
    resolve(__dirname, '..', 'templates', 'model.hbs'),
    resolve(__dirname, 'templates', 'model.hbs'),
  ];
  for (const p of candidates) {
    if (existsSync(p)) return p;
  }
  throw new Error('Could not find model.hbs template in package');
}

function updateRenderersIndex(renderersDir: string): void {
  const filePath = join(renderersDir, 'index.ts');
  if (!existsSync(filePath)) {
    console.error(`  warn  ${filePath} not found, skipping renderers setup`);
    return;
  }

  let content = readFileSync(filePath, 'utf-8');
  if (content.includes('litedbmodel-gen')) {
    console.log(`  skip  ${filePath} (already configured)`);
    return;
  }

  const importLine = "import { litedbmodelColumns } from 'litedbmodel-gen';";

  const entry = `  litedbmodel_columns: {
    ...litedbmodelColumns,
    dependsOn: ['schema'],
  },`;

  if (content.includes('import ')) {
    const lastImportIdx = content.lastIndexOf('import ');
    const lineEnd = content.indexOf('\n', lastImportIdx);
    content = content.slice(0, lineEnd + 1) + importLine + '\n' + content.slice(lineEnd + 1);
  } else {
    content = importLine + '\n\n' + content;
  }

  content = insertIntoObject(content, 'embeds', entry);

  writeFileSync(filePath, content, 'utf-8');
  console.log(`  update  ${filePath}`);
}

function updateDatasourcesIndex(datasourcesDir: string): void {
  const filePath = join(datasourcesDir, 'index.ts');
  if (!existsSync(filePath)) {
    console.error(`  warn  ${filePath} not found, skipping datasources setup`);
    return;
  }

  let content = readFileSync(filePath, 'utf-8');
  if (content.includes('litedbmodel-gen')) {
    console.log(`  skip  ${filePath} (already configured)`);
    return;
  }

  const importLine = "import { sqlSchema } from 'litedbmodel-gen';";

  const entry = '  sql_schema: sqlSchema,';

  if (content.includes('import ')) {
    const lastImportIdx = content.lastIndexOf('import ');
    const lineEnd = content.indexOf('\n', lastImportIdx);
    content = content.slice(0, lineEnd + 1) + importLine + '\n' + content.slice(lineEnd + 1);
  } else {
    content = importLine + '\n\n' + content;
  }

  content = insertIntoObject(content, 'datasourceTypes', entry);

  writeFileSync(filePath, content, 'utf-8');
  console.log(`  update  ${filePath}`);
}

function insertIntoObject(source: string, objectName: string, entry: string): string {
  const pattern = new RegExp(`(export\\s+const\\s+${objectName}\\s*=\\s*\\{)([^}]*)(\\})`);
  const match = source.match(pattern);
  if (!match) return source;

  const [, opening, body, closing] = match;
  const trimmedBody = body.replace(/\s+/g, ' ').trim();

  let newBody: string;
  if (trimmedBody === '' || trimmedBody.startsWith('//')) {
    newBody = `\n${entry}\n`;
  } else {
    newBody = body.trimEnd() + '\n' + entry + '\n';
  }

  return source.replace(pattern, `${opening}${newBody}${closing}`);
}

function updateConfig(configPath: string): void {
  let content = readFileSync(configPath, 'utf-8');

  if (content.includes('sql_schema')) {
    console.log('  skip  embedoc.config.yaml (sql_schema already configured)');
    return;
  }

  const datasourceBlock = `  schema:
    type: sql_schema
    path: "./db/schema.sql"
    database: PostgreSQL
    generators:
      - output_path: "./models/{model_class}.ts"
        template: model.hbs
        overwrite: false`;

  if (content.includes('datasources: {}')) {
    content = content.replace('datasources: {}', 'datasources:\n' + datasourceBlock);
  } else if (content.includes('datasources:')) {
    const idx = content.indexOf('datasources:');
    const lineEnd = content.indexOf('\n', idx);
    content = content.slice(0, lineEnd + 1) + datasourceBlock + '\n' + content.slice(lineEnd + 1);
  } else {
    content += '\ndatasources:\n' + datasourceBlock + '\n';
  }

  if (!content.includes('models/**/*.ts')) {
    const targetsInsert = `  - pattern: "./models/**/*.ts"
    comment_style: block`;

    if (content.includes('targets:')) {
      const idx = content.indexOf('targets:');
      const lineEnd = content.indexOf('\n', idx);
      content = content.slice(0, lineEnd + 1) + targetsInsert + '\n' + content.slice(lineEnd + 1);
    }
  }

  writeFileSync(configPath, content, 'utf-8');
  console.log('  update  embedoc.config.yaml');
}

createProgram(handlers, pkg.version).parse();
